import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/shared/api/axios';
import { Card, CardHeader, CardBody } from '@/shared/components/ui/Card';
import { Button } from '@/shared/components/ui/Button';
import { Select } from '@/shared/components/ui/Select';
import { Modal } from '@/shared/components/ui/Modal';
import { Skeleton } from '@/shared/components/ui/Skeleton';
import { useUser } from '@/shared/hooks/useAuth';
import { AlertTriangle, Plus } from 'lucide-react';
import toast from 'react-hot-toast';

interface Warning {
  _id: string;
  employeeId: { _id: string; display_name: string; department?: string };
  category: string;
  reason: string;
  issuedBy: { _id: string; display_name: string };
  issuedAt: string;
}

function useWarnings() {
  return useQuery({
    queryKey: ['hr', 'warnings'],
    queryFn: async () => { const { data } = await api.get('/api/hr-performance/warnings'); return data as { data: Warning[]; total: number }; },
  });
}

function useMyWarnings() {
  return useQuery({
    queryKey: ['hr', 'my-warnings'],
    queryFn: async () => { const { data } = await api.get('/api/hr-performance/warnings/mine'); return data as { data: Warning[]; total: number }; },
  });
}

function useEmployeeList() {
  return useQuery({
    queryKey: ['hr', 'employees-list'],
    // /api/v2/hr/employees never existed (404) — the staff list is GET /api/users.
    queryFn: async () => { const { data } = await api.get<{ users?: { id: string; display_name: string; department?: string }[] }>('/api/users'); return (data?.users ?? []).map((u) => ({ _id: u.id, display_name: u.display_name, department: u.department })); },
  });
}

function useIssueWarning() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: { employeeId: string; category: string; reason: string }) => {
      const { data } = await api.post('/api/hr-performance/warnings', payload);
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['hr', 'warnings'] }),
  });
}

const CATEGORIES = [
  { value: 'late_attendance', label: 'Late Attendance' },
  { value: 'policy_violation', label: 'Policy Violation' },
  { value: 'performance_issue', label: 'Performance Issue' },
  { value: 'misconduct', label: 'Misconduct' },
  { value: 'other', label: 'Other' },
];

export function PerformancePage() {
  const user = useUser();
  const isHrAdmin = user?.role === 'superadmin' || user?.role === 'admin' || (user?.department === 'hr' && ['manager', 'team_lead'].includes(user?.role ?? ''));

  return (
    <div className="p-6 space-y-4">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Performance & Warnings</h1>
        <p className="text-sm text-muted-foreground mt-1">Track employee performance issues and warnings. Employees with 3+ warnings trigger an alert to SuperAdmin.</p>
      </div>
      {isHrAdmin ? <AdminWarningsView /> : <EmployeeWarningsView />}
    </div>
  );
}

export function AdminWarningsView() {
  const { data, isLoading } = useWarnings();
  const issueMut = useIssueWarning();
  const { data: employees } = useEmployeeList();
  const [showNew, setShowNew] = useState(false);
  const [employeeId, setEmployeeId] = useState('');
  const [category, setCategory] = useState('');
  const [reason, setReason] = useState('');

  const handleIssue = async () => {
    if (!employeeId || !category || !reason.trim()) { toast.error('All fields are required.'); return; }
    try {
      const result = await issueMut.mutateAsync({ employeeId, category, reason: reason.trim() });
      toast.success(`Warning issued.${result.alertTriggered ? ' ⚠️ SA alerted (3+ warnings).' : ''}`);
      setShowNew(false); setEmployeeId(''); setCategory(''); setReason('');
    } catch (err: any) { toast.error(err?.response?.data?.error || 'Failed to issue warning.'); }
  };

  if (isLoading) return <Skeleton className="h-[300px]" />;
  const warnings = data?.data || [];

  // Group by employee to show count badges
  const countByEmployee: Record<string, number> = {};
  warnings.forEach(w => { const id = w.employeeId?._id || ''; countByEmployee[id] = (countByEmployee[id] || 0) + 1; });

  return (
    <>
      <Modal open={showNew} onClose={() => setShowNew(false)} title="Issue Warning" footer={
        <div className="flex justify-end gap-2"><Button variant="ghost" onClick={() => setShowNew(false)}>Cancel</Button><Button onClick={handleIssue} disabled={issueMut.isPending}>{issueMut.isPending ? 'Issuing...' : 'Issue Warning'}</Button></div>
      }>
        <div className="space-y-3 py-3">
          <Select name="w-employee" label="Employee" value={employeeId} onChange={e => setEmployeeId(e.target.value)} options={[{ value: '', label: 'Select employee...' }, ...(employees || []).map(e => ({ value: e._id, label: e.display_name }))]} />
          <Select name="w-category" label="Category" value={category} onChange={e => setCategory(e.target.value)} options={[{ value: '', label: 'Select category...' }, ...CATEGORIES]} />
          <div><label className="mb-1.5 block text-sm font-medium">Reason *</label><textarea className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm bg-white text-gray-900" rows={3} value={reason} onChange={e => setReason(e.target.value)} placeholder="Describe the issue..." /></div>
          <p className="text-xs text-muted-foreground">When an employee reaches 3 warnings, SuperAdmin is automatically notified.</p>
        </div>
      </Modal>

      <Card className="border-none shadow-sm">
        <CardHeader title={`All Warnings (${warnings.length})`} action={<Button size="sm" onClick={() => setShowNew(true)}><Plus className="h-4 w-4 mr-1" />Issue Warning</Button>} />
        <CardBody className="p-0">
          <div className="overflow-auto max-h-[500px]">
            <table className="w-full text-sm text-left">
              <thead className="text-xs uppercase bg-muted/50 sticky top-0">
                <tr><th className="px-4 py-3">Employee</th><th className="px-4 py-3">Category</th><th className="px-4 py-3">Reason</th><th className="px-4 py-3">Issued By</th><th className="px-4 py-3">Date</th><th className="px-4 py-3">Count</th></tr>
              </thead>
              <tbody className="divide-y">
                {warnings.length === 0 && <tr><td colSpan={6} className="px-4 py-8 text-center text-muted-foreground">No warnings issued yet.</td></tr>}
                {warnings.map(w => (
                  <tr key={w._id} className="hover:bg-muted/20">
                    <td className="px-4 py-3 font-medium">{w.employeeId?.display_name || '-'}</td>
                    <td className="px-4 py-3"><span className="px-2 py-0.5 rounded-full text-xs font-medium bg-warning/10 text-warning-text capitalize">{w.category?.replace('_', ' ')}</span></td>
                    <td className="px-4 py-3 max-w-[200px] truncate">{w.reason}</td>
                    <td className="px-4 py-3 text-muted-foreground">{w.issuedBy?.display_name || '-'}</td>
                    <td className="px-4 py-3">{new Date(w.issuedAt).toLocaleDateString('en-IN')}</td>
                    <td className="px-4 py-3">
                      {(countByEmployee[w.employeeId?._id] || 0) >= 3 ? (
                        <span className="flex items-center gap-1 text-danger-text font-bold"><AlertTriangle className="h-3.5 w-3.5" />{countByEmployee[w.employeeId?._id]}</span>
                      ) : (
                        <span>{countByEmployee[w.employeeId?._id]}</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardBody>
      </Card>
    </>
  );
}

export function EmployeeWarningsView() {
  const { data, isLoading } = useMyWarnings();
  if (isLoading) return <Skeleton className="h-[300px]" />;
  const warnings = data?.data || [];

  return (
    <Card className="border-none shadow-sm">
      <CardHeader title={`My Warnings (${warnings.length})`} />
      <CardBody className="p-0">
        <div className="overflow-auto max-h-[500px]">
          {warnings.length === 0 ? (
            <p className="px-4 py-8 text-center text-muted-foreground">You have no warnings. Keep up the good work!</p>
          ) : (
            <table className="w-full text-sm text-left">
              <thead className="text-xs uppercase bg-muted/50 sticky top-0">
                <tr><th className="px-4 py-3">Category</th><th className="px-4 py-3">Reason</th><th className="px-4 py-3">Date</th></tr>
              </thead>
              <tbody className="divide-y">
                {warnings.map(w => (
                  <tr key={w._id} className="hover:bg-muted/20">
                    <td className="px-4 py-3"><span className="px-2 py-0.5 rounded-full text-xs font-medium bg-warning/10 text-warning-text capitalize">{w.category?.replace('_', ' ')}</span></td>
                    <td className="px-4 py-3">{w.reason}</td>
                    <td className="px-4 py-3">{new Date(w.issuedAt).toLocaleDateString('en-IN')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        {warnings.length >= 3 && (
          <div className="p-3 border-t bg-danger/10 text-danger-text text-sm flex items-center gap-2">
            <AlertTriangle className="h-4 w-4" /> You have {warnings.length} warnings. Please discuss with your manager.
          </div>
        )}
      </CardBody>
    </Card>
  );
}
